import { apiList3LastDayHistories, apiListHistories } from 'apis/SensorApi';
import FanLoadingIcon from 'components/FanLoadingIcon';
import Icon, { IconList } from 'components/Icon';
import { ITEM_NUM_PER_PAGE } from 'constants/config';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE, TEXT_DESCRIPTION, TEXT_ELTRES_ID, TEXT_GROUP_ID, TEXT_LATITUDE, TEXT_LOCATION, TEXT_LONGITUDE, TEXT_MANAGEMENT_ID, TEXT_NAME, TEXT_REGISTERED_DATE, TEXT_STATUS_DETAIL } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { SensorType } from 'models';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { FLOOD_GATE_MESSAGES, SENSOR_PROPERTIES, UNDEFINED_TEXT } from 'utils/constanst';
import { modifiedValue } from 'utils/function';
import GeneralSensorHistoryChart from './GeneralSensorHistoryChart';
import HistoryChart from './HistoryChart';
import "./SensorDetailView.css";
import SensorHistoryList from './SensorHistoryList';

const SENSOR_LEFT_INFO = [
  {
    id: SENSOR_PROPERTIES.MANAGE_ID,
    label: TEXT_MANAGEMENT_ID
  },
  {
    id: SENSOR_PROPERTIES.NAME,
    label: TEXT_NAME
  },
  {
    id: SENSOR_PROPERTIES.LOCATION,
    label: TEXT_LOCATION
  },
  {
    id: SENSOR_PROPERTIES.CREATEDAT,
    label: TEXT_REGISTERED_DATE,
  },
  {
    id: SENSOR_PROPERTIES.DESCRIPTION,
    label: TEXT_DESCRIPTION
  }
]

const SENSOR_RIGHT_INFO = [
  {
    id: SENSOR_PROPERTIES.ID,
    label: TEXT_ELTRES_ID
  },
  {
    id: SENSOR_PROPERTIES.API_GROUP_ID,
    label: TEXT_GROUP_ID
  },
  {
    id: SENSOR_PROPERTIES.LAT,
    label: TEXT_LATITUDE
  },
  {
    id: SENSOR_PROPERTIES.LONG,
    label: TEXT_LONGITUDE
  },
  {
    id: SENSOR_PROPERTIES.ALL_STATUSES,
    label: TEXT_STATUS_DETAIL
  }
]

const propTypes = {
  sensor: PropTypes.object,
  targetGroup: PropTypes.object,
  onBack: PropTypes.func
};
const defaultProps = {
  sensor: {},
  targetGroup: PropTypes.object,
  onBack: () => { }
};

export const ReturnBtn = ({ onClick }) => {
  return (
    <div className='sensor_detail_view-flex_row comeback-btn-container' >
      <button className='sensor_detail_view-flex_row comeback-btn' onClick={onClick}>
        <Icon icon={IconList.iconReturn} />
        <label style={{ color: '#1d679d' }}>一覧に戻る</label>
      </button>
    </div>
  )
}

ReturnBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
}
ReturnBtn.defaultProps = {
  onClick: () => { },
}


export const SensorItem = ({ sensor, field, targetGroup }) => {
  return (
    <div className='sensor_detail_view-flex_row sensor_detail_view_info'>
      {
        field.map((info, index) => {
          if (info.id === SENSOR_PROPERTIES.ALL_STATUSES && targetGroup?.type === SensorType.GENERAL) return null;
          return (
            <div key={index} className='sensor_detail_view-flex_row sensor_detail_view_item'>
              <label className='label-text'>{info.label}</label>
              <label className='sensor_detail_view_value_text'>
                {targetGroup?.type === SensorType.FLOOD_GATE && info.id === SENSOR_PROPERTIES.ALL_STATUSES ?
                  sensor.data.allStatuses?.map(s => FLOOD_GATE_MESSAGES[s]).filter(s => !!s).join('、') :
                  modifiedValue(sensor[info.id], info.id)}
              </label>
            </div>
          )
        })
      }
    </div>
  )
}

SensorItem.propTypes = {
  sensor: PropTypes.object,
  field: PropTypes.array,
  targetGroup: PropTypes.object,
}

SensorItem.defaultProps = {
  sensor: {},
  field: [],
  targetGroup: {},
}

const SensorDetailView = (props) => {
  /*** States and Variables ***/
  const { sensor, targetGroup } = props;
  const [histories, setHistories] = React.useState([]);
  const { sensorGroups } = useSensorGroups();
  const nextTokenRef = React.useRef(null);
  const [curPage, setCurPage] = React.useState(1);
  const [pageNum, setPageNum] = React.useState(1);
  const { error } = useDialog();
  const [loading, setLoading] = React.useState(false);
  const [chartData, setChartData] = React.useState([]);

  const filterHistories = targetGroup?.type === SensorType.GENERAL ? { ge: moment(sensor.data.historyPeriod).startOf('day').toISOString() } : {}

  //handle Modified data
  const handleHistoryInfo = React.useCallback((item) => {
    const messageInfo = targetGroup && targetGroup.messages && targetGroup.messages[item.status];
    const statusValue = {
      color: '#000',
      content: UNDEFINED_TEXT,
    };
    if (messageInfo && item.status >= 0) {
      statusValue.color = messageInfo.color;
      statusValue.content = messageInfo.status;
    }
    return statusValue;
  }, [sensorGroups, sensor]);

  /*** Processing ***/
  React.useEffect(() => {
    const downloadHistories = async () => {
      const result = await apiListHistories(sensor.id, nextTokenRef.current, filterHistories);
      if (result.success) {
        const modifiedData = result.data?.map((data) => {
          const statusValue = handleHistoryInfo(data);
          return {
            ...data,
            statusValue,
          }
        });
        setHistories(modifiedData);
        nextTokenRef.current = result.nextToken;
        if (result.nextToken) {
          setPageNum((prev) => prev + 1);
        }
      }
      else {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    }
    const downloadGraphData = async () => {
      setLoading(true);
      const result = await apiList3LastDayHistories(sensor.id);
      if (result.success) {
        setChartData(result.data);
      }
      else {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
      setLoading(false);
    }
    downloadHistories();
    downloadGraphData();
    return () => {
      setHistories([]);
      nextTokenRef.current = null;
    }
  }, [sensor.id])

  React.useEffect(() => {
    return () => {
      setHistories([]);
      nextTokenRef.current = null;
    }
  }, [])
  /*** Sub Components ***/
  /*** Event Handlers ***/
  const onPageChange = async (newPage) => {
    if (pageNum === newPage && nextTokenRef.current) {
      const result = await apiListHistories(sensor.id, nextTokenRef.current, filterHistories);
      if (result.success) {
        const modifiedData = result.data?.map((data) => {
          const statusValue = handleHistoryInfo(data);
          return {
            ...data,
            statusValue,
          }
        });
        histories.push(...modifiedData);
        setHistories(Object.assign([], histories));
        nextTokenRef.current = result.nextToken;
        if (result.nextToken) {
          setPageNum((prev) => prev + 1);
        }
      }
      else {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      }
    }
    setCurPage(newPage);
  }

  /*** Main Render ***/
  return (
    <div className='sensor_detail_view_container'>
      <div className='sensor_detail_view-flex_row sensor_detail_main_info'>
        <SensorItem sensor={sensor} targetGroup={props.targetGroup} field={SENSOR_LEFT_INFO} />
        <SensorItem sensor={sensor} targetGroup={props.targetGroup} field={SENSOR_RIGHT_INFO} />
      </div>
      <ReturnBtn onClick={props.onBack} />
      <div className='sensor_detail_view_chart_container'>
        {props.targetGroup.type === SensorType.FLOOD_GATE &&
          <HistoryChart sensor={sensor} historyItems={chartData} />}
        {props.targetGroup.type === SensorType.GENERAL && (
          <GeneralSensorHistoryChart sensor={sensor} historyItems={chartData} />
        )}
        {loading ? <FanLoadingIcon strokeWidth={4} /> : null}
      </div>
      <div className='sensor_detail_view_history_list_container'>
        <SensorHistoryList
          pageNum={pageNum}
          sensor={sensor}
          sensorHistories={histories}
          curPage={curPage}
          onPageChange={onPageChange}
          itemNumPerPage={ITEM_NUM_PER_PAGE}
          isNew
        />
      </div>
      <ReturnBtn onClick={props.onBack} />
    </div>
  )
}
SensorDetailView.propTypes = propTypes;
SensorDetailView.defaultProps = defaultProps;
export default SensorDetailView;